/* eslint-disable */
import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useSelector } from 'react-redux';
import { Panel } from 'primereact/panel';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { useQuery } from '@apollo/react-hooks';
import { Dropdown } from 'primereact/dropdown';
import { Loading } from '../../components/Loading';
import { growlShape } from '../../components/prop-types';
import { withGrowl } from '../../components/GrowlNotifications';
import { withAccount } from '../../components/AccountContext';
import { growlErrors } from '../../utils/NotificationUtil';
import Content from './Content';
import {
  QUESTIONNAIRE_TRANSLATED,
  JPS_TRANSLATED,
  REPORT_TEXTS_TRANSLATED,
  VALIDATED,
  SECTION,
  SURVEY,
  INTERESTS_SECTION,
} from './constants';
import UpdateConversionNorm from './UpdateConversionNorm';

const LOAD_DATA_ACTION_TYPE = 'LOAD_DATA';
const SET_LANGUAGE_ACTION_TYPE = 'SET_LANGUAGE';
const SET_CONTENT_CATEGORY_ACTION_TYPE = 'SET_CONTENT_CATEGORY';
const SHOW_DIALOG = 'SHOW_DIALOG';
const HIDE_DIALOG = 'HIDE_DIALOG';

const orderAlphabetically = (a, b) => {
  if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
  if (b.label.toLowerCase() > a.label.toLowerCase()) return -1;
  return 0;
};

const i18nMessages = defineMessages({
  updateTranslations: {
    id: 'menu.item-bank-management.update-translations',
    defaultMessage: 'Update Translations',
  },
  dimension: {
    id: 'menu.item-bank-management.dimension',
    defaultMessage: 'Dimension',
  },
  competency: {
    id: 'menu.item-bank-management.competency',
    defaultMessage: 'Competency',
  },
  ability: {
    id: 'menu.item-bank-management.ability',
    defaultMessage: 'Ability',
  },
  questionnaire: {
    id: 'itembank-management.questionnaire',
    defaultMessage: 'Questionnaire',
  },
  reportTexts: {
    id: 'itembank-management.report-texts',
    defaultMessage: 'Report Texts',
  },
  jobProfileSurvey: {
    id: 'itembank-management.job-profile-survey',
    defaultMessage: 'Job Profile Survey',
  },
  validated: {
    id: 'itembank-management.validated',
    defaultMessage: 'Validated',
  },
  conversionNorm: {
    id: 'itembank-management.conversion-norm',
    defaultMessage: 'Conversion Norm',
  },
});

const CONTENT_CATEGORY_AND_ACTIVE_LOCALES_QUERY = gql`
  query contentCategoryAndActiveLocales($id: ID!) {
    activeLocales
    contentCategories(id: $id) {
      sectionContentCategories {
        code
        displayName
        type
        availableCategories
        content {
          code
          displayName
          questionnaireLanguages
          jpsLanguages
          reportLanguages
          validatedLanguages
          conversionNorms {
            language
            conversionNorm
          }
        }
      }
      surveyContentCategory {
        code
        displayName
        type
        availableCategories
        content {
          code
          displayName
          content {
            code
            displayName
            questionnaireLanguages
            reportLanguages
          }
        }
      }
    }
  }
`;

const EnableContent = ({ currentAccount, growl }) => {
  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const { formatMessage } = useIntl();

  const DIALOG_INITIAL_STATE = {
    visible: false,
    sectionCode: null,
    dimensionCode: null,
    conversionNorm: null,
    language: null,
  };

  const INITIAL_STATE = {
    dialog: DIALOG_INITIAL_STATE,
    selectedLanguage: null,
    selectedContentCategory: {
      content: [],
      availableCategories: [],
    },
    supportedLanguages: [],
    availableContentCategories: {
      sectionContentCategories: [],
      surveyContentCategory: null,
    },
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case LOAD_DATA_ACTION_TYPE:
        return {
          ...state,
          selectedLanguage: action.payload.selectedLanguage,
          selectedContentCategory: action.payload.selectedContentCategory,
          supportedLanguages: action.payload.supportedLanguages,
          availableContentCategories: action.payload.availableContentCategories,
        };
      case SET_LANGUAGE_ACTION_TYPE:
        return {
          ...state,
          selectedLanguage: action.payload.selectedLanguage,
        };
      case SET_CONTENT_CATEGORY_ACTION_TYPE:
        return {
          ...state,
          selectedContentCategory: action.payload.selectedContentCategory,
        };
      case SHOW_DIALOG:
        return {
          ...state,
          dialog: {
            visible: true,
            sectionCode: action.payload.sectionCode,
            dimensionCode: action.payload.dimensionCode,
            conversionNorm: action.payload.conversionNorm,
            language: action.payload.language,
          },
        };
        case HIDE_DIALOG:
          return {
            ...state,
            dialog: DIALOG_INITIAL_STATE,
          };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const { loading, error, data } = useQuery(CONTENT_CATEGORY_AND_ACTIVE_LOCALES_QUERY, {
    variables: { id: currentAccount },
  });

  const { activeLocales, contentCategories } = data;

  const { dialog, selectedLanguage, selectedContentCategory, supportedLanguages, availableContentCategories } = state;

  const { content, availableCategories } = selectedContentCategory;

  useEffect(() => {
    const dataIsAvailable = activeLocales && activeLocales.length > 0 && contentCategories;

    if (dataIsAvailable) {
      const languages = activeLocales
        .map((lang) => ({
          value: lang,
          label: localeDisplayName(lang),
        }))
        .sort(orderAlphabetically);

      const contentCategory = getContentCategory();

      const action = {
        type: LOAD_DATA_ACTION_TYPE,
        payload: {
          selectedLanguage: selectedLanguage || languages[0].value,
          selectedContentCategory: contentCategory,
          supportedLanguages: languages,
          availableContentCategories: contentCategories,
        },
      };

      dispatch(action);
    }
  }, [activeLocales, contentCategories]);

  const doesSelectedContentCategoryExist = () => {
    return selectedContentCategory && content.length !== 0 && availableCategories.length !== 0;
  };

  const getContentCategory = () => {
    const selectedContentCategoryExists = doesSelectedContentCategoryExist();

    if (selectedContentCategoryExists) {
      const sectionContentCategory = contentCategories.sectionContentCategories.find(
        (contentCategory) => contentCategory.code === selectedContentCategory.code,
      );

      const surveyContentCategory =
        contentCategories.surveyContentCategory.code === selectedContentCategory.code
          ? contentCategories.surveyContentCategory
          : null;
      if (sectionContentCategory) {
        return { ...selectedContentCategory, content: sectionContentCategory.content };
      }

      if (surveyContentCategory) {
        return { ...selectedContentCategory, content: surveyContentCategory.content };
      }

      return contentCategories.sectionContentCategories[0];
    }

    return contentCategories.sectionContentCategories[0];
  };

  const getCategoryOptions = () => {
    const categoryOptions = [];

    const sectionContentCategories = availableContentCategories.sectionContentCategories.map((contentCategory) => ({
      value: contentCategory.code,
      label: contentCategory.displayName,
    }));

    const surveyContentCategory = {
      value: availableContentCategories.surveyContentCategory
        ? availableContentCategories.surveyContentCategory.code
        : '',
      label: availableContentCategories.surveyContentCategory
        ? availableContentCategories.surveyContentCategory.displayName
        : '',
    };

    categoryOptions.push(...sectionContentCategories);
    categoryOptions.push(surveyContentCategory);

    return categoryOptions;
  };

  const changeSelectedCategory = (event) => {
    const { value: selectedContentCategoryCode } = event;

    const sectionContentCategory = contentCategories.sectionContentCategories.find(
      (contentCategory) => contentCategory.code === selectedContentCategoryCode,
    );

    const surveyContentCategory =
      contentCategories.surveyContentCategory.code === selectedContentCategoryCode
        ? contentCategories.surveyContentCategory
        : null;

    let selectedCategory = null;

    if (sectionContentCategory) {
      selectedCategory = sectionContentCategory;
    }

    if (surveyContentCategory) {
      selectedCategory = surveyContentCategory;
    }

    const action = {
      type: SET_CONTENT_CATEGORY_ACTION_TYPE,
      payload: {
        selectedContentCategory: selectedCategory,
      },
    };

    dispatch(action);
  };

  const changeSelectedLanguage = (event) => {
    const action = {
      type: SET_LANGUAGE_ACTION_TYPE,
      payload: {
        selectedLanguage: event.value,
      },
    };

    dispatch(action);
  };

  const showDialog = (sectionCode, dimensionCode, conversionNorm, language) => {
    const action = {
      type: SHOW_DIALOG,
      payload: {
        sectionCode,
        dimensionCode,
        conversionNorm,
        language
      },
    };

    dispatch(action);
  };

  const hideDialog = () => {
    const action = {
      type: HIDE_DIALOG
    };

    dispatch(action);
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const categoryOptions = getCategoryOptions();

  return (
    <>
      <UpdateConversionNorm
        accountId={currentAccount}
        dialog={dialog}
        hideDialog={hideDialog}
      />
      <div className="brand-detailed-wrapper">
        <div className="wrapper p-grid">
          <div className="p-col-12">
            <Panel
              header={
                <div className="update-translations-panel-header">
                  <FormattedMessage id="menu.item-bank-management.enable-content" defaultMessage="Enable Content" />
                  <div className="dropdowns">
                    <Dropdown
                      filter
                      filterBy="label"
                      value={selectedContentCategory.code}
                      options={categoryOptions}
                      onChange={changeSelectedCategory}
                    />
                    <Dropdown
                      filter
                      filterBy="label"
                      value={selectedLanguage}
                      options={supportedLanguages}
                      onChange={changeSelectedLanguage}
                    />
                  </div>
                </div>
              }
            >
              <div className="enable-content">
                <div className="header">
                  {selectedContentCategory.type === SECTION && (
                    <div className="section-component-title">{formatMessage(i18nMessages.dimension)}</div>
                  )}
                  {selectedContentCategory.type === SURVEY && (
                    <div className="survey-component-title">
                      <div>{formatMessage(i18nMessages.competency)}</div>
                      <div>{formatMessage(i18nMessages.ability)}</div>
                    </div>
                  )}
                  <div className="available-categories">
                    {availableCategories.includes(QUESTIONNAIRE_TRANSLATED) && (
                      <div>{formatMessage(i18nMessages.questionnaire)}</div>
                    )}
                    {availableCategories.includes(REPORT_TEXTS_TRANSLATED) && (
                      <div>{formatMessage(i18nMessages.reportTexts)}</div>
                    )}
                    {availableCategories.includes(JPS_TRANSLATED) && (
                      <div>{formatMessage(i18nMessages.jobProfileSurvey)}</div>
                    )}
                    {availableCategories.includes(VALIDATED) && <div>{formatMessage(i18nMessages.validated)}</div>}
                    {selectedContentCategory.type === SECTION && selectedContentCategory.code !== INTERESTS_SECTION && (
                      <div>{formatMessage(i18nMessages.conversionNorm)}</div>
                    )}
                  </div>
                </div>
                <div className="components">
                  {selectedContentCategory.type === SECTION &&
                    content.map((contentRow) => (
                      <Content
                        key={content.code}
                        accountId={currentAccount}
                        selectedLanguage={selectedLanguage}
                        selectedContentCategory={selectedContentCategory}
                        content={contentRow}
                        showDialog={showDialog}
                      />
                    ))}
                  {selectedContentCategory.type === SURVEY &&
                    content.map((competency) => {
                      return competency.content.map((ability) => (
                        <Content
                          key={ability.code}
                          accountId={currentAccount}
                          selectedLanguage={selectedLanguage}
                          selectedContentCategory={{
                            code: competency.code,
                            displayName: competency.displayName,
                            type: selectedContentCategory.type,
                            availableCategories: selectedContentCategory.availableCategories,
                            content: competency.content,
                          }}
                          content={ability}
                        />
                      ));
                    })}
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </>
  );
};

EnableContent.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withGrowl(withAccount(EnableContent));
